import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials } from '../types';
import { authService } from '../services/api/auth';
import { clearStoredLastActivity, signalLogoutToOtherTabs } from '../hooks/useIdleTimeout';
import { clearAllWizardDrafts } from '../utils/cohortWizardDraft';
import { useImpersonationStore } from './impersonation';
import { usePersonaStore } from './persona';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // _hasRehydrated: localStorage has been read by Zustand persist (triggers initializeAuth)
  _hasRehydrated: boolean;
  // _hasHydrated: auth state is validated and ready for routing decisions
  _hasHydrated: boolean;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  refreshTokens: () => Promise<boolean>;
  clearError: () => void;
  setHasHydrated: (state: boolean) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
}

type AuthStore = AuthState & AuthActions;

// MDF-239: impersonation and the active persona live in sessionStorage, not in
// auth-storage, so they outlive the tokens. Every login and logout clears them,
// or the next login in the same tab inherits the impersonation banner.
export function clearSessionScopedState(): void {
  useImpersonationStore.getState().clearImpersonation();
  usePersonaStore.setState({ activePersona: 'admin', isAdmin: false, isFullAdmin: false });
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasRehydrated: false,
      _hasHydrated: false,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(credentials);

          clearSessionScopedState();
          set({
            user: response.user,
            accessToken: response.tokens.accessToken,
            refreshToken: response.tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          return true;
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Login failed';
          set({
            isLoading: false,
            error: message,
            isAuthenticated: false,
          });

          return false;
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        const impersonation = useImpersonationStore.getState();
        const originalRefreshToken = impersonation.originalRefreshToken;
        // MED-1383: the base user of this tab (the admin when impersonating),
        // for the logout signal to the other tabs.
        const baseUserId = impersonation.originalUser?.id ?? get().user?.id;
        // MED-1383: a session stored before the mode existed falls back to
        // the old comparison.
        const isBackendImpersonation = impersonation.mode
          ? impersonation.mode === 'backend'
          : refreshToken !== originalRefreshToken;

        try {
          if (impersonation.isImpersonating && originalRefreshToken && isBackendImpersonation) {
            // MDF-239: end the server-side impersonation, which revokes the
            // impersonation token and closes the impersonation_log row, then
            // revoke both admin tokens. Each step runs on its own, so one
            // failed call does not leave the later tokens valid.
            // A request first lets the client refresh an expiring token, so
            // the token sent below is the live one, not a rotated-out copy.
            await authService.getCurrentUser().catch(() => undefined);
            const liveRefreshToken = get().refreshToken;
            try {
              const ended = await authService.endImpersonate(
                liveRefreshToken || undefined,
                originalRefreshToken
              );
              set({
                accessToken: ended.tokens.accessToken,
                refreshToken: ended.tokens.refreshToken,
              });
              await authService.logout(ended.tokens.refreshToken).catch(() => undefined);
            } catch {
              if (liveRefreshToken) {
                await authService.logout(liveRefreshToken).catch(() => undefined);
              }
              // No refresh token in state, so the client cannot rotate the
              // original admin token before the revoke below.
              set({ accessToken: impersonation.originalAccessToken, refreshToken: null });
            }
            await authService.logout(originalRefreshToken).catch(() => undefined);
          } else if (refreshToken) {
            await authService.logout(refreshToken);
          }
        } catch {
          // Ignore logout errors - still clear local state
        }

        // Clear idle timeout tracking
        clearStoredLastActivity();
        clearSessionScopedState();
        // MED-1268: wizard drafts can hold a list of unsaved time slots. They
        // must not show to the next user of this browser.
        clearAllWizardDrafts(localStorage);

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
        signalLogoutToOtherTabs(baseUserId);
      },

      initializeAuth: async () => {
        const { accessToken, _hasHydrated } = get();

        // Prevent double initialization (e.g., from React StrictMode or rapid re-renders)
        if (_hasHydrated) return;

        if (!accessToken) {
          set({ _hasHydrated: true });
          return;
        }

        // An admin tab writes its own tokens to the shared auth-storage, so after
        // a reload this tab can hold admin tokens under a stale impersonation
        // flag. The impersonation tokens are gone; end it here, without the API.
        const impersonation = useImpersonationStore.getState();
        if (
          impersonation.isImpersonating &&
          impersonation.mode === 'backend' &&
          readTokenClaims(accessToken)?.identity.endsWith(':')
        ) {
          clearSessionScopedState();
          // The tab is still on the impersonated user's page, which shows
          // that user's layout to the admin. Open the admin start page.
          if (window.location.pathname !== '/') {
            window.location.replace('/');
            return;
          }
        }

        try {
          const user = await authService.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            _hasHydrated: true,
          });
        } catch {
          // Token invalid, try to refresh
          const refreshed = await get().refreshTokens();
          if (!refreshed) {
            // Refresh failed — if impersonating, try to restore admin state
            const impersonationStore = useImpersonationStore.getState();
            if (impersonationStore.isImpersonating && impersonationStore.originalUser) {
              const original = impersonationStore.endImpersonation();
              if (original) {
                set({
                  user: original.originalUser,
                  accessToken: original.originalAccessToken,
                  refreshToken: original.originalRefreshToken,
                  isAuthenticated: true,
                  _hasHydrated: true,
                });
                return;
              }
            }
            // Not impersonating or restore failed — clear auth
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              _hasHydrated: true,
            });
          }
        }
      },

      refreshTokens: async () => {
        const { refreshToken } = get();

        if (!refreshToken) {
          return false;
        }

        try {
          const response = await authService.refreshToken(refreshToken);

          set({
            accessToken: response.tokens.accessToken,
            refreshToken: response.tokens.refreshToken,
          });

          // Re-fetch user data
          const user = await authService.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            _hasHydrated: true,
          });

          return true;
        } catch {
          return false;
        }
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken });
      },

      setAuth: (user: User, accessToken: string, refreshToken: string) => {
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          error: null,
        });
      },

      clearError: () => set({ error: null }),

      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => state => {
        // Only mark localStorage read as complete. Do NOT set _hasHydrated here.
        // _hasHydrated is set by initializeAuth after the token is validated,
        // preventing ProtectedRoute from passing users through with stale auth.
        if (state) {
          state._hasRehydrated = true;
        }
      },
    }
  )
);

// MED-1383: read the identity and issue time from a JWT payload. Decode only:
// the API verifies every token it receives.
function readTokenClaims(token: string): { identity: string; iat: number } | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      userId?: string;
      impersonatingUserId?: string;
      iat?: number;
    };
    if (!json.userId || typeof json.iat !== 'number') return null;
    return { identity: `${json.userId}:${json.impersonatingUserId ?? ''}`, iat: json.iat };
  } catch {
    return null;
  }
}

// MED-1383: all tabs share auth-storage, but each tab keeps its own copy of
// the tokens in memory. A refresh in one tab rotates the refresh token and
// revokes the old one, so another tab that keeps the old copy fails its next
// refresh and clears auth-storage, which logs out every tab. Take the new
// pair when another tab writes it, but only when:
// - it is for the same identity, so a different user or impersonation is
//   ignored;
// - its refresh token was issued later than this tab's (iat), because a stale
//   tab can write an old, revoked pair at any time (Zustand writes on every
//   set());
// - this tab is not in a server-side impersonation. Two tabs can impersonate
//   the same user in separate sessions, and the tokens do not say which
//   session they belong to. Ending one session would then revoke the pair
//   that the other tab took.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.storageArea !== localStorage || event.key !== 'auth-storage' || !event.newValue) {
      return;
    }
    try {
      const next = (
        JSON.parse(event.newValue) as {
          state?: { accessToken?: string | null; refreshToken?: string | null };
        }
      ).state;
      const current = useAuthStore.getState();
      if (!current.isAuthenticated || !current.refreshToken) return;
      if (!next?.accessToken || !next.refreshToken) return;
      if (next.refreshToken === current.refreshToken) return;
      const impersonation = useImpersonationStore.getState();
      if (impersonation.isImpersonating && impersonation.mode !== 'frontend') return;
      const nextClaims = readTokenClaims(next.refreshToken);
      const currentClaims = readTokenClaims(current.refreshToken);
      if (!nextClaims || !currentClaims) return;
      if (nextClaims.identity !== currentClaims.identity) return;
      if (nextClaims.iat <= currentClaims.iat) return;
      current.setTokens(next.accessToken, next.refreshToken);
    } catch {
      // An unreadable value from another tab is ignored.
    }
  });
}
